import {
  forwardRef,
  MouseEvent,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, VBox } from "./Box";
import { Icon } from "./Icon";
import { classNames } from "@/utils/classNames";
import styles from "./Details.module.css";
import { BgColor } from "./colors";
import { BoxProps } from "./boxHelpers";

export type DetailsProps = {
  title: ReactElement | string | number;
  actions?: ReactElement | string | number;
  content: ReactNode;
  open?: boolean;
  onToggle?: (open: boolean) => void;
  borderTop?: boolean;
  borderBottom?: boolean;
  bg?: BgColor | boolean;
  titleBoxProps?: BoxProps;
  contentBoxProps?: BoxProps;
  boxShadow?: boolean;
  caretPosition?: "left" | "right";
};

export const Details = forwardRef<HTMLDetailsElement, DetailsProps>(function Details(
  {
    title,
    actions,
    content,
    onToggle,
    borderTop,
    borderBottom,
    bg,
    titleBoxProps,
    contentBoxProps,
    boxShadow,
    caretPosition = "right",
    ...props
  },
  ref,
) {
  const [isOpen, setIsOpen] = useState(props.open ?? false);
  const contentRef = useRef<HTMLDivElement>(null);
  const rotation = isOpen ? 180 : 0;
  const handleToggle = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    if (isOpen) {
      const endState = !isOpen;
      contentRef.current?.classList.add(styles.closing);
      contentRef.current?.addEventListener(
        "transitionend",
        () => {
          contentRef.current?.classList.remove(styles.closing);
          setIsOpen(endState);
          onToggle?.(endState);
        },
        { once: true },
      );
    } else {
      setIsOpen(!isOpen);
      onToggle?.(!isOpen);
    }
  };

  useEffect(() => {
    if (props.open !== undefined) {
      setIsOpen(props.open);
    }
  }, [props.open]);

  return (
    <details
      ref={ref}
      open={isOpen}
      className={classNames(styles.details, boxShadow && "shadow-md")}
    >
      <summary className={"block select-none"} onClick={handleToggle}>
        <Box
          alignItems={"center"}
          paddingX
          paddingY
          bg={bg ?? true}
          className={"hover:bg-alt2 cursor-pointer"}
          borderTop={borderTop}
          borderBottom={!isOpen && borderBottom}
          {...titleBoxProps}
        >
          {caretPosition === "left" && (
            <Icon
              name={"caret-right"}
              className={classNames(["transition", isOpen ? "rotate-90" : "rotate-0"])}
            />
          )}
          <div className={"mr-auto"}>{title}</div>
          {actions}
          {caretPosition === "right" && <Icon name={"caret-down"} rotate={rotation} />}
        </Box>
      </summary>
      <Box
        ref={contentRef}
        className={classNames(styles.content)}
        vertical
        bg={bg ?? true}
        borderBottom={borderBottom}
        paddingX
        {...contentBoxProps}
      >
        {content}
      </Box>
    </details>
  );
});

export type DetailsGroupProps = {
  borderTop?: boolean;
  borderBottom?: boolean;
  panels: DetailsProps[];
};

export const DetailsGroup = forwardRef<HTMLDivElement, DetailsGroupProps>(
  function DetailsGroup(props, ref) {
    return (
      <VBox ref={ref} gap={0}>
        {preparePanels(props).map((p, i) => (
          <Details key={i} {...p} />
        ))}
      </VBox>
    );
  },
);

function preparePanels({
  panels,
  borderTop = false,
  borderBottom = false,
}: DetailsGroupProps): DetailsProps[] {
  return panels.map((p, i) => ({
    ...p,
    borderTop: i !== 0 || borderTop,
    borderBottom: i === panels.length - 1 || borderBottom,
  }));
}
